import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { SafeHtmlPipe } from '@app/shared/pipes/safe-html.pipe';
import { TooltipOptions } from 'primeng/api';
import { TooltipModule } from 'primeng/tooltip';
import { getTranslationProvider } from 'src/app/core/i18n/getTranslationProvider';
import { TranslatableComponent } from 'src/app/core/i18n/TranslatableComponent';
import { TranslatableStringType } from 'src/app/core/i18n/types';
import { environment } from 'src/environments/environment';

type SubscriptionTypeIconComponentTranslations = Record<
  number,
  {
    title: TranslatableStringType;
    description: TranslatableStringType;
  }
>;

@Component({
  selector: 'app-subscription-type-icon',
  template: `<div
      *ngIf="!typeId"
      class="h-8 w-full"
      [pTooltip]="tooltipContent"
      [tooltipOptions]="tooltipOptions"></div>
    <div class="flex justify-center" *ngIf="typeId">
      <a [href]="pageUrl" target="_blank">
        <img
          [src]="iconUrl"
          class="iconsize hover:border-b border-bl-blue-600"
          [pTooltip]="tooltipContent"
          [tooltipOptions]="tooltipOptions" />
      </a>
    </div>
    <ng-template #tooltipContent>
      <div class="font-semibold mb-3">{{ tooltip.title }}</div>
      <div [innerHTML]="tooltip.description | safeHtml"></div>
    </ng-template> `,
  styles: [
    `
      img.iconsize {
        height: 24px;
      }
    `,
  ],
  standalone: true,
  imports: [CommonModule, TooltipModule, SafeHtmlPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [getTranslationProvider('subscriptionTypeIconComponent')],
})
export class SubscriptionTypeIconComponent extends TranslatableComponent {
  @Input() typeId: number;
  @Input() cloudApiKey: string;

  tooltipOptions: TooltipOptions = { escape: false, tooltipStyleClass: 'subscription-type-tooltip' };
  private baseIconUrl = 'assets/blapp-subscription-type-icons/sub_type';
  private i18n: SubscriptionTypeIconComponentTranslations;

  get pageUrl(): string {
    return `${environment.blappWeb}/${this.cloudApiKey || ''}?lang=${this.getActiveLanguage()}`;
  }

  get iconUrl(): string {
    return `${this.baseIconUrl}${this.typeId}.png`;
  }

  get tooltip(): { title: string; description: string } {
    return this.i18n[this.typeId] || this.i18n[0] || { title: '', description: '' };
  }

  constructor(private changeDetectorRef: ChangeDetectorRef) {
    super();
    this.i18n = {} as SubscriptionTypeIconComponentTranslations;
  }

  protected override onTranslationsLoaded(): void {
    const translateSubscriptionType = (key: number) => ({
      title: this.translate(`${key}.title`),
      description: this.translate(`${key}.description`),
    });

    this.i18n = {
      0: translateSubscriptionType(0),
      1: translateSubscriptionType(1),
      2: translateSubscriptionType(2),
      3: translateSubscriptionType(3),
      4: translateSubscriptionType(4),
      5: translateSubscriptionType(5),
      6: translateSubscriptionType(6),
    };

    this.changeDetectorRef.markForCheck();
  }
}
